import { fetcher } from '#lib/backendFetcher';

/**
 * Fetches prospect queries with optional filtering and pagination.
 * @param {Object} [options]
 * @param {Array<string>} [options.queryTypeCodes] - Filter by query types
 * @param {number} [options.page] - Page number
 * @param {number} [options.limit] - Entries per page
 * @param {boolean} [options.includeCounts=true] - Whether to include lead counts
 * @returns {Promise<{data: Array<Object>, error: null, totalCount: number}>} Owned and shared
 *   queries merged into one list; shared queries carry `isShared: true` and `sharedBy`.
 */
export const fetchProspectQueries = async ({
	queryTypeCodes = null,
	page = null,
	limit = null,
	includeCounts = true,
} = {}) => {
	const params = new URLSearchParams();

	if (queryTypeCodes && queryTypeCodes.length > 0) {
		params.set('queryTypeCodes', queryTypeCodes.join(','));
	}
	if (page !== null) params.set('page', String(page));
	if (limit !== null) params.set('pageSize', String(limit));
	if (includeCounts) params.set('includeCounts', 'true');

	const queryString = params.toString();
	const url = `/api/v1/leads/queries${queryString ? `?${queryString}` : ''}`;

	const result = await fetcher(url);

	// Merge owned and shared queries into one list. The backend returns them in separate
	// arrays; the selector distinguishes them via the `isShared` flag (own = falsy).
	const ownQueries = (result.queries ?? []).map((q) => ({ ...q, isShared: false }));
	const sharedQueries = (result.sharedQueries ?? []).map((q) => ({
		...q,
		isShared: true,
		sharedBy: q.sharedBy ?? q.sharedByUserId ?? null,
	}));

	return {
		data: [...ownQueries, ...sharedQueries],
		error: null,
		totalCount: result.total ?? ownQueries.length,
	};
};

/**
 * Loads one prospect query; unlike the list it always carries `agentRunId`.
 *
 * @param {string} prospectQueryId - UUID of the prospect query
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const fetchProspectQuery = async (prospectQueryId) => {
	try {
		const data = await fetcher(`/api/v1/leads/queries/${prospectQueryId}`);
		return { data, error: null };
	} catch (error) {
		return { data: null, error };
	}
};

/**
 * Creates a new prospect query.
 * @param {string} queryName - Name of the prospect query
 * @param {string} queryTypeCode - Query type (e.g. 'googleMapsScraper', 'localHrSearch')
 * @param {Object} [input={}] - Search parameters depending on query type
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const createProspectQuery = async (queryName, queryTypeCode, input = {}) => {
	try {
		const data = await fetcher('/api/v1/leads/queries', {
			method: 'POST',
			body: { queryName, input, queryType: queryTypeCode },
		});
		return { data, error: null };
	} catch (error) {
		return { data: null, error };
	}
};

/**
 * Updates an existing prospect query.
 * @param {string} prospectQueryId - UUID of the prospect query
 * @param {Object} updates - Fields to update
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const updateProspectQuery = async (prospectQueryId, updates) => {
	try {
		const data = await fetcher(`/api/v1/leads/queries/${prospectQueryId}`, {
			method: 'PATCH',
			body: updates,
		});
		return { data, error: null };
	} catch (error) {
		return { data: null, error };
	}
};

/**
 * Deletes a prospect query and all associated data.
 * @param {string} prospectQueryId - UUID of the prospect query
 * @returns {Promise<{error: Error|null}>}
 */
export const deleteProspectQuery = async (prospectQueryId) => {
	try {
		await fetcher(`/api/v1/leads/queries/${prospectQueryId}`, {
			method: 'DELETE',
		});
		return { error: null };
	} catch (error) {
		return { error };
	}
};

/**
 * Merges two prospect queries (leads from source into target).
 * @param {string} sourceId - UUID of the source prospect query
 * @param {string} targetId - UUID of the target prospect query
 * @returns {Promise<Object>}
 */
export const mergeProspectQueries = async (sourceId, targetId) => {
	return await fetcher('/api/v1/leads/queries/merge', {
		method: 'POST',
		body: { sourceId, targetId },
	});
};
